import React from 'react';
import { FormTitle } from '@molecules/index';
import { UploadPhoto } from '@molecules/UploadPhoto/UploadPhoto';
import { ErrorMessages } from '@types';
import { Link } from '@atoms/Link/Link';
import { PdfProvider, usePdfConfig } from '@shared/utils';

type UploadPhotoFormProps = {
  id?: string;
  formTitle?: string;
  description?: string | React.ReactNode;
  handleFileChange: (files: File[]) => void | Promise<void>;
  handleUploadError: (error: string) => void;
  handleDelete?: (files: File[]) => void | Promise<void>;
  maxNumberOfUpload?: number;
  errorPath?: string;
  errorMessages?: ErrorMessages[];
  initialFiles?: File[];
  dropzoneLabel?: string;
  acceptedFileTypes?: string[];
};

export const UploadPhotoForm: React.FC<UploadPhotoFormProps> = ({
  id,
  formTitle,
  description,
  handleFileChange,
  handleUploadError,
  handleDelete,
  maxNumberOfUpload = 1,
  errorPath,
  errorMessages,
  initialFiles,
  dropzoneLabel,
  acceptedFileTypes,
}) => {
  const { isPdfAllowed } = usePdfConfig();
  return (
    <div>
      {formTitle && <FormTitle text={formTitle} subtext={description} />}
      <PdfProvider isPdfAllowed={isPdfAllowed}>
        <UploadPhoto
          // id set to errorPath so that error can navigate to the correct upload photo
          id={errorPath ?? id}
          handleFileChange={handleFileChange}
          handleUploadError={handleUploadError}
          handleDelete={handleDelete}
          maxNumberOfUpload={maxNumberOfUpload}
          errorPath={errorPath}
          errorMessages={errorMessages}
          initialFiles={initialFiles}
          dropzoneLabel={dropzoneLabel}
          acceptedFileTypes={acceptedFileTypes ?? undefined}
        />
      </PdfProvider>
    </div>
  );
};

export const UploadPhotoTextContent = () => {
  return (
    <div className="text-bodyLg my-4 flex flex-col gap-4">
      <p>
        Photos help us locate and assess the issue.{' '}
        <Link
          href="https://www.westminster.gov.uk/data-protection/report-it-image-disclaimer"
          className="text-action-link underline"
          linkType="inline"
          external={true}
        >
          Find out how we will be using your image.
        </Link>
      </p>

      <p>
        You can upload up to five images. You can upload JPEG, PNG, GIF or WebP
        files.
      </p>
    </div>
  );
};
